import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import {
  formatGameTimer,
  parseStageTimerSeconds,
  resolveCountdownElapsedSeconds,
} from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './team-anthem.css';

const STAGE_NUMBER = 6;
const COMPLETION_POINTS = 1;
const RESPONSE_MAX = 500;
const DEFAULT_PLAY_SECONDS = 300;
const STAGE2_PLAY_DEADLINE_PREFIX = 'stage2_play_deadline';
const MIC_ICON = '/img/mic.png';

function stage2PlayDeadlineKey(userId, sessionId) {
  return `${STAGE2_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage2PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage2PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage2PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/\.\s+/g, '.\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function ClockIcon() {
  return (
    <svg className="s2-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

export default function Stage2({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(2);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage2 = themeData?.stages_data?.stage_6;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage2?.timer, DEFAULT_PLAY_SECONDS),
    [stage2?.timer]
  );

  const logoUrl = s3PublicUrl(stage2?.logo ?? stage2?.badge);
  const rulesList = getStageRulesList(stage2);
  const title = String(stage2?.title ?? stage2?.name ?? 'Stage 2');
  const descriptionText = useMemo(
    () => formatStageDescription(stage2?.description),
    [stage2?.description]
  );

  const isConfigured = !!stage2 && String(stage2?.title ?? '').trim().length > 0;

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);
  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's2'), [visuals]);

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const [response, setResponse] = useState('');
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [micFailed, setMicFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage2,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;
    if (String(response ?? '').trim() === '' && !auto) {
      triggerShake();
      setMessage('Please enter your response');
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = COMPLETION_POINTS;
    const trimmedAnswer = String(response).trim();

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    const daysForReveal = Math.max(1, Number(completedDaysForReveal) || 1);
    setRevealCompletedDays(daysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  const revealOverlay = showRevealOverlay ? (
    <div
      className="stage-reveal-overlay stage-reveal-overlay--stage2"
      role="dialog"
      aria-modal="true"
      aria-label="Revealing your puzzle progress"
      onClick={handleRevealOverlayClick}
    >
      <div className="stage-reveal-confetti" aria-hidden>
        {revealConfettiPieces.map((piece) => (
          <span
            key={piece.id}
            className="stage-reveal-confetti-piece"
            style={{
              left: piece.left,
              animationDelay: piece.delay,
              animationDuration: piece.duration,
            }}
          />
        ))}
      </div>
      <div className="stage-reveal-stage">
        <aside
          className="stage-page-puzzle-aside s2-panel s2-panel--puzzle stage-reveal-panel"
          aria-label="Puzzle pieces progress"
        >
          <StagePuzzleReveal
            key={`stage2-reveal-${revealCompletedDays ?? STAGE_NUMBER}`}
            themeData={themeData}
            completedDaysOverride={revealCompletedDays ?? STAGE_NUMBER}
            animateReveal
          />
        </aside>
      </div>
    </div>
  ) : null;

  return (
    <div className={`stage2-page${showRevealOverlay ? ' stage2-page--reveal' : ''}`} style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 6 is not configured: set stage_6 title and description in the theme.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage2-header-main">
            <header className="s2-top-bar">
              <div className="s2-top-timer" aria-label="Stage timer" aria-live="polite">
                <ClockIcon />
                <span className="s2-timer-value">{countdownDisplay}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage2-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>

            <header className="stage2-hero">
              <div className="stage2-logo-wrap">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className="stage2-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <div className="stage2-logo-fallback" aria-hidden>
                    Stage 2
                  </div>
                )}
              </div>
              <h1 className="stage2-title">{title}</h1>
            </header>
          </div>

          <div className="s2-play-area">
            <div className="stage-page-layout s2-main-row">

              <section className="s2-panel s2-panel--description" aria-label="Stage description">
                <p className="stage2-label s2-description-label">Example:</p>
                <p className="stage2-description stage2-description--promo">
                  {descriptionText ||
                    'Your team is about to enter the grand finale.'}
                </p>
              </section>

              <div className="s2-panel s2-panel--input">
                {!submitted ? (
                  <div className="stage2-form">
                    <label className="stage2-label" htmlFor="stage2-response">
                      Your anthem
                    </label>
                    <textarea
                      id="stage2-response"
                      className={`stage2-textarea ${inputShake ? 'stage2-textarea--shake' : ''}`}
                      value={response}
                      onChange={(ev) => {
                        setResponse(ev.target.value.slice(0, RESPONSE_MAX));
                        if (message) setMessage('');
                      }}
                      maxLength={RESPONSE_MAX}
                      rows={6}
                      placeholder="Write your rap, chant, poem or anthem here…"
                      disabled={submitted}
                      aria-describedby="stage2-char-count"
                    />
                    <div className="stage2-form-footer">
                      <div id="stage2-char-count" className="stage2-char-count">
                        {response.length}/{RESPONSE_MAX}
                      </div>
                      {!micFailed ? (
                        <img
                          src={MIC_ICON}
                          alt=""
                          className="stage2-mic-deco"
                          aria-hidden
                        />
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <p className="stage2-complete">Thanks! Your response has been submitted.</p>
                )}

                {message ? <div className="stage2-hint">{message}</div> : null}
              </div>

              {!showRevealOverlay ? (
                <aside
                  className="stage-page-puzzle-aside s2-panel s2-panel--puzzle"
                  aria-label="Puzzle pieces progress"
                >
                  <StagePuzzleReveal themeData={themeData} />
                </aside>
              ) : null}
            </div>

            {!submitted ? (
              <form className="s2-submit-wrap" onSubmit={handleSubmit}>
                <button
                  type="submit"
                  className="stage2-submit"
                  disabled={submitted || String(response ?? '').trim() === ''}
                >
                  Submit anthem
                </button>
              </form>
            ) : null}
          </div>

          {revealOverlay ? createPortal(revealOverlay, document.body) : null}
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage2-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage2-rules-title"
          onClick={closeRules}
        >
          <div className="stage2-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage2-rules-title">How to play</h2>
            <ul className="stage2-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage2-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
